import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  VisuallyHidden,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconMail, IconUserPlus } from "@tabler/icons-react";
import { useState } from "react";
import { semanticColor } from "@how/design-system";
import { ROLE_TEMPLATES } from "@how/permissions";
import { EmptyState, ErrorState, LoadingState, StatusBadge, useReturnFocus } from "@how/ui";
import { z } from "zod";
import {
  describeInviteError,
  useChurchLogins,
  useInviteOwner,
  type ChurchLogin,
} from "./church-owners";

/** A role's name: a system role's from the registry; a church's custom role shows its key, as the database has it. */
export function roleLabel(key: string): string {
  return (
    ROLE_TEMPLATES.find((r) => r.key === key)?.name ??
    (key.startsWith("custom_") ? `Custom: ${key.slice(7).replace(/_/g, " ")}` : key)
  );
}

const emailSchema = z.email().trim();

export interface OwnersCardProps {
  readonly tenantId: string;
  readonly tenantName: string;
}

/**
 * Owners and staff logins on a church's page (ADR 0064), for a platform super admin: who can sign in to the church,
 * with their roles and where they stand. DCX invites an owner, and sends an invited owner's email again. Suspending
 * and removing a login stay in the church's own Team screen.
 */
export function OwnersCard({ tenantId, tenantName }: OwnersCardProps) {
  const logins = useChurchLogins(tenantId);
  const invite = useInviteOwner(tenantId);
  const returnFocus = useReturnFocus();
  const [inviting, setInviting] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [resending, setResending] = useState<string | null>(null);

  const close = (): void => {
    setInviting(false);
    returnFocus.restore();
  };

  const sendInvitation = (): void => {
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setEmailError("Enter the owner's email address");
      return;
    }
    setInviteError(null);
    invite.mutate(
      { email: parsed.data },
      {
        onSuccess: () => {
          notifications.show({
            color: semanticColor("success"),
            title: "Invitation sent",
            message: `${parsed.data} is invited as an owner of ${tenantName}.`,
          });
          close();
        },
        onError: (failure) => {
          setInviteError(describeInviteError(failure));
        },
      },
    );
  };

  const resend = (login: ChurchLogin): void => {
    setResending(login.membershipId);
    invite.mutate(
      { email: login.email, resend: true },
      {
        onSuccess: () => {
          notifications.show({
            color: semanticColor("success"),
            title: "Invitation sent again",
            message: `The invitation went to ${login.email} again.`,
          });
        },
        onError: (failure) => {
          notifications.show({
            color: semanticColor("danger"),
            title: "The invitation was not sent",
            message: describeInviteError(failure),
          });
        },
        onSettled: () => {
          setResending(null);
        },
      },
    );
  };

  const owners = (logins.data ?? []).filter(
    (l) => l.roles.includes("tenant_owner") && l.status === "active",
  );

  return (
    <Card>
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={2} size="h4">
              Owners and staff logins
            </Title>
            <Text size="sm" c="dimmed">
              {logins.isSuccess
                ? owners.length === 0
                  ? "No active owner yet."
                  : `${String(owners.length)} active ${owners.length === 1 ? "owner" : "owners"}.`
                : "Who can sign in to this church."}{" "}
              Suspending or removing a login is done by the church, in Team.
            </Text>
          </div>
          <Button
            variant="default"
            leftSection={<IconUserPlus size={16} />}
            onClick={() => {
              returnFocus.remember();
              setEmail("");
              setEmailError(null);
              setInviteError(null);
              setInviting(true);
            }}
          >
            Invite an owner
          </Button>
        </Group>
        {logins.isPending ? (
          <LoadingState rows={3} />
        ) : logins.isError ? (
          <ErrorState
            message="Could not load this church's logins."
            onRetry={() => {
              void logins.refetch();
            }}
          />
        ) : logins.data.length === 0 ? (
          <EmptyState
            title="Nobody can sign in yet"
            description="Invite the church's owner: they get an email to set up their login."
          />
        ) : (
          <Table.ScrollContainer minWidth={640}>
            <Table aria-label={`${tenantName} logins`}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Email</Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Roles</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>
                    <VisuallyHidden>Actions</VisuallyHidden>
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {logins.data.map((login) => (
                  <Table.Tr key={login.membershipId}>
                    <Table.Td>{login.email}</Table.Td>
                    <Table.Td>{login.displayName === "" ? "—" : login.displayName}</Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        {login.roles.map((r) => (
                          <Badge key={r} variant="light" color={semanticColor("neutral")}>
                            {roleLabel(r)}
                          </Badge>
                        ))}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <StatusBadge domain="membership_status" value={login.status} />
                    </Table.Td>
                    <Table.Td>
                      {login.status === "invited" && login.roles.includes("tenant_owner") ? (
                        <Button
                          size="xs"
                          variant="default"
                          leftSection={<IconMail size={14} />}
                          aria-label={`Send the invitation again to ${login.email}`}
                          loading={invite.isPending && resending === login.membershipId}
                          disabled={invite.isPending && resending !== login.membershipId}
                          onClick={() => {
                            resend(login);
                          }}
                        >
                          Send the invitation again
                        </Button>
                      ) : null}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Stack>

      <Modal opened={inviting} onClose={close} title={`Invite an owner of ${tenantName}`}>
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            sendInvitation();
          }}
        >
          <Stack gap="sm">
            <Text size="sm">
              They are sent an email to set up their login and become an owner. If the address
              already has a HoW login, the invitation waits for them in the staff dashboard the next
              time they sign in.
            </Text>
            <TextInput
              label="Email"
              type="email"
              required
              data-autofocus
              value={email}
              error={emailError}
              onChange={(event) => {
                const value = event.currentTarget.value;
                setEmail(value);
                setEmailError(null);
              }}
            />
            {inviteError === null ? null : (
              <Alert color={semanticColor("danger")} variant="light" role="alert">
                {inviteError}
              </Alert>
            )}
            <Group justify="flex-end">
              <Button variant="default" onClick={close}>
                Cancel
              </Button>
              <Button type="submit" loading={invite.isPending}>
                Send invitation
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Card>
  );
}
